import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Calendar,
  Clock,
  Video,
  User,
  ArrowLeft,
  ArrowRight,
  Check,
  Star,
  MapPin,
  Languages,
  CreditCard,
  Shield,
  Users,
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { FetchbookedTime, FetchPublicProviders, Postappointments } from '../../../api/helper';
import { setAppointmentData } from '../../../redux/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { ShowToast } from '../../../helpers/ToastService';

const appointmentTypes = [
  { value: 'initial', label: 'Initial Consultation', duration: '30 min', price: '$200' },
  { value: 'followup', label: 'Follow-up Appointment', duration: '30 min', price: '$120' },
  { value: 'medication', label: 'Medication Management', duration: '30 min', price: '$120' },
  { value: 'therapy', label: 'Therapy Session', duration: '30 min', price: '$180' },
  { value: 'crisis', label: 'Crisis Consultation', duration: '30 min', price: '$150' }
];

export const TelehealthBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch()
  const appointmentData = useSelector((state) => state?.sliceReducer.appointmentData);
  const profiledata = useSelector((state) => state?.cookies?.userDetails);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerdata, setproviderdata] = useState([])
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    if (location.state?.providerdata) {
      setSelectedProvider(location.state.providerdata);
      setCurrentStep(2);
    }
    if (appointmentData) {
      setSelectedProvider(appointmentData.provider || null);
      setSelectedDate(appointmentData.appointmentdate || '');
      setSelectedTime(appointmentData.appointmenttime || '');
      setAppointmentType(appointmentData.appointmenttype || '');
      setReasonForVisit(appointmentData.appointmentreason || '');
      setCurrentStep(4);
    }
  }, [location, appointmentData]);

  useEffect(() => {
    if (location.state?.step === 4) {
      setCurrentStep(4);
    }
  }, [location]);

  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const GetProvder = async (page = 1, search = '') => {
    try {
      setLoadingMore(true);
      const payload = {
        page,
        limit: 10,
        search,
      };
      const respo = await FetchPublicProviders(payload);
      const fetchedData = respo?.data?.data?.data || [];
      const pagination = respo?.data?.data?.pagination;
      if (page === 1) {
        setproviderdata(fetchedData);
      } else {
        setproviderdata(prev => [...prev, ...fetchedData]);
      }
      setCurrentPage(pagination?.page || 1);
      setTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.log('Error fetching providers:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    GetProvder(1, debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const loadMoreProviders = () => {
    if (currentPage < totalPages && !loadingMore) {
      GetProvder(currentPage + 1, debouncedSearchTerm);
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setCurrentStep(2);
  };
  const generateTimeSlots = (startTime, endTime) => {
    const slots = [];

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const start = new Date();
    start.setHours(startHour, startMin, 0, 0);

    const end = new Date();
    end.setHours(endHour, endMin, 0, 0);

    while (start < end) {
      const slotTime = start.toTimeString().slice(0, 5); // "HH:MM"
      slots.push({ time: slotTime, available: true });
      start.setMinutes(start.getMinutes() + 30);
    }

    return slots;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const selectedDay = new Date(date).toLocaleDateString('en-US', { weekday: 'long' });
    const daySchedule = selectedProvider?.availableDays?.availableDays?.find(day => day.day === selectedDay);
    if (daySchedule?.shifts?.length > 0) {
      const allSlots = [];
      daySchedule.shifts.forEach((shift) => {
        const slots = generateTimeSlots(shift.startTime, shift.endTime);
        allSlots.push(...slots);
      });
      setTimeSlots(allSlots);
    } else {
      setTimeSlots([]);
    }
    setSelectedTime('');
    const payload = {
      providerId: selectedProvider._id,
      date: date
    };
    Getbookedtime(payload);
  };


  const generateAvailableDates = () => {
    if (!selectedProvider?.availableDays?.availableDays) return [];

    const availableDaysList = selectedProvider.availableDays.availableDays.map(day => day.day); // e.g. ['Tuesday', 'Thursday', ...]

    const dates = [];
    const today = new Date();

    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

      if (availableDaysList.includes(dayOfWeek)) {
        dates.push({
          date: date.toISOString().split('T')[0],
          display: date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          }),
          dayOfWeek: dayOfWeek
        });
      }
    }

    return dates;
  };
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setCurrentStep(3);
  };

  const handleBookAppointment = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours);
    startDate.setMinutes(minutes);
    const formatTime = (date) => {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const appointment = {
      patientId: profiledata?._id,
      doctorId: selectedProvider?._id,
      date: selectedDate,
      startTime: formatTime(startDate),
      reason: reasonForVisit,
      name: profiledata?.firstName,
      Total: "10",
      BookingFee: "2",
      GrandTotal: "12",
      status: 'Requested',
      Duration: '30 minutes'
    };
    console.log(appointment)
    // return
    if (profiledata) {

      const respones = await Postappointments(appointment);
      ShowToast(respones.data.message, "success");
      setSelectedProvider(null);
      setCurrentStep(1);
      navigate("/Dashboradpatient");
    } else {
      const appointmentdata = {
        provider: selectedProvider,
        appointmentdate: selectedDate,
        appointmenttime: selectedTime,
        appointmenttype: appointmentType,
        appointmentreason: reasonForVisit,
        status: 'confirmed',
        meetingLink: `https://meet.mindcare.com/room/${Date.now()}`
      };
      dispatch(setAppointmentData(appointmentdata));
      navigate('/login', {
        state: { from: '/booking/telehealth', step: 4 }
      });
    }

    setLoading(false);
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Choose Provider';
      case 2: return 'Select Date & Time';
      case 3: return 'Appointment Details';
      case 4: return 'Confirmation';
      default: return 'Book Appointment';
    }
  };

  function getNextAvailableInDaysFromDate(dateStr) {
    try {
      const now = new Date();
      const availableDate = new Date(dateStr);
      const diffTime = availableDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `Within ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } catch {
      return "not available";
    }
  }

  function getNextAvailableInDays(availableDays) {
    if (!Array.isArray(availableDays) || availableDays.length === 0) {
      return "not available";
    }
    const daysOfWeek = [
      "Sunday", "Monday", "Tuesday", "Wednesday",
      "Thursday", "Friday", "Saturday"
    ];
    const today = new Date();
    const todayIndex = today.getDay();
    const deltas = availableDays.map((dayObj) => {
      const index = daysOfWeek.indexOf(dayObj.day);
      if (index === -1) return 7;
      let delta = index - todayIndex;
      if (delta < 0) delta += 7;
      return delta;
    });
    const minDelta = Math.min(...deltas);
    return `Within ${minDelta} day${minDelta !== 1 ? 's' : ''}`;
  }

const Getbookedtime = async (payload) => {
  console.log(payload,"payload===")
  try {
    const respo = await FetchbookedTime(payload);

    if (respo?.data?.success) {
      const booked = respo.data.data;

      const bookedTimes = booked.map(item => {
        const [hours, minutes] = item.startTime.split(":").map(Number);
        const start = new Date(0, 0, 0, hours, minutes);

        let durationMinutes = 0;
        if (item.Duration.includes("30")) {
          durationMinutes = 30;
        } else if (item.Duration.includes("60") || item.Duration.includes("1 hour")) {
          durationMinutes = 60;
        }

        const end = new Date(start.getTime() + durationMinutes * 60000);

        return { start, end };
      });

      const updatedSlots = timeSlots.map(slot => {
        const [slotHours, slotMinutes] = slot.time.split(":").map(Number);
        const slotTime = new Date(0, 0, 0, slotHours, slotMinutes);

        const isBooked = bookedTimes.some(({ start, end }) => slotTime >= start && slotTime < end);

        return {
          ...slot,
          available: !isBooked,
        };
      });

      setTimeSlots(updatedSlots);
    }
  } catch (error) {
    console.log(error);
  }
};
// console.log(timeSlots,"timeSlots===")
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-6 border-l border-gray-300"></div>
            <h1 className="text-3xl text-gray-900">Book Telehealth Appointment</h1>
          </div>
          <div className="flex items-center space-x-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${step <= currentStep
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-300 text-gray-400'
                  }`}>
                  {step < currentStep ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span>{step}</span>
                  )}
                </div>
                {step < 4 && (
                  <div className={`w-12 h-0.5 mx-2 ${step < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-gray-600 mt-4">{getStepTitle()}</p>
        </div>
        {currentStep === 1 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <span>Choose Your Provider</span>
                </CardTitle>
                <CardDescription>
                  Select a mental health professional who best fits your needs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {providerdata?.map((provider) => {
                  return (
                    <div
                      key={provider?._id}
                      className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      onClick={() => handleProviderSelect(provider)}>
                      <div className="flex items-center space-x-4">
                        {provider?.image ? (
                          <ImageWithFallback
                            src={provider.image}
                            alt={`${provider?.firstName} ${provider?.lastName}`}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-16 h-16 rounded-full object-cover text-gray-400 bg-gray-100 p-4" />
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg text-gray-900">
                            {provider?.firstName || ''} {provider?.lastName || ''}
                          </h3>
                          <p className="text-gray-600">
                            {provider?.Specialties?.[0]?.name || provider?.specialty || 'General Practice'}
                          </p>
                          <div className="flex items-center space-x-4 mt-2">
                            <div className="flex items-center space-x-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="text-sm text-gray-900">{provider?.rating || '4.5'}</span>
                              <span className="text-sm text-gray-500">({provider?.reviewCount || '12'})</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {provider?.practiceLocation || provider?.address?.city || 'Unknown Location'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Languages className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {provider?.LanguagesId?.map((lang) => lang?.name).join(', ') || 'English'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary">
                            Next:{" "}
                            {provider?.nextAvailable
                              ? getNextAvailableInDaysFromDate(provider.nextAvailable)
                              : getNextAvailableInDays(provider?.availableDays?.availableDays)}
                          </Badge>
                          <Button className="mt-2">Select Provider</Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {currentPage < totalPages && (
                  <div className="flex justify-center mt-4">
                    <Button onClick={loadMoreProviders} disabled={loadingMore}>
                      {loadingMore ? "Loading..." : "Load More"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        {currentStep === 2 && selectedProvider && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Select Date & Time</span>
                </CardTitle>
                <CardDescription>
                  Choose when you'd like to meet with {selectedProvider.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg text-gray-900 mb-4">Available Dates</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {generateAvailableDates().map((dateObj) => (
                        <button
                          key={dateObj.date}
                          onClick={() => handleDateSelect(dateObj.date)}
                          className={`p-3 text-left border rounded-lg transition-colors ${selectedDate === dateObj.date
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}>
                          <div className="text-sm text-gray-600">{dateObj.dayOfWeek}</div>
                          <div className="text-gray-900">{dateObj.display}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg text-gray-900 mb-4">Available Times</h3>
                    {selectedDate ? (
                      <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                        {timeSlots.map((slot, index) => (
                          <button
                            key={index}
                            onClick={() => slot.available && handleTimeSelect(slot.time)}
                            disabled={!slot.available}
                            className={`p-3 text-center border rounded-lg transition-colors ${slot.available
                              ? selectedTime === slot.time
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                              : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                              }`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">Please select a date first</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {currentStep === 3 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Video className="h-5 w-5" />
                  <span>Appointment Details</span>
                </CardTitle>
                <CardDescription>
                  Provide information about your visit
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="appointmentType">Type of Appointment</Label>
                  <Select value={appointmentType} onValueChange={setAppointmentType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select appointment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {appointmentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center justify-between w-full">
                            <span>{type.label}</span>
                            <div className="flex items-center space-x-2 ml-4">
                              <span className="text-xs text-gray-500">{type.duration}</span>
                              <span className="text-xs text-gray-700">{type.price}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="reason">Reason for Visit</Label>
                  <Textarea
                    id="reason"
                    placeholder="Please describe what you'd like to discuss during your appointment..."
                    value={reasonForVisit}
                    onChange={(e) => setReasonForVisit(e.target.value)}
                    className="h-24"
                  />
                </div>
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(2)}
                    className="flex-1"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(4)}
                    disabled={!appointmentType || !reasonForVisit.trim()}
                    className="flex-1"
                  >
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {currentStep === 4 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Check className="h-5 w-5" />
                  <span>Confirm Your Appointment</span>
                </CardTitle>
                <CardDescription>
                  Review your appointment details before confirming
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg text-gray-900 mb-4">Appointment Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Provider:</span>
                      <span className="text-gray-900">{selectedProvider?.firstName + " " + selectedProvider?.lastName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="text-gray-900">
                        {new Date(selectedDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="text-gray-900">{selectedTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="text-gray-900">
                        {appointmentTypes.find(t => t.value === appointmentType)?.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="text-gray-900">
                        {appointmentTypes.find(t => t.value === appointmentType)?.duration}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t pt-4">
                      <span className="text-gray-900">Total Cost:</span>
                      <span className="text-xl text-gray-900">
                        {appointmentTypes.find(t => t.value === appointmentType)?.price}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Shield className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">Secure Payment</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Payment will be processed after your appointment. We accept most insurance plans and offer flexible payment options.
                  </p>
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Insurance verification in progress...</span>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(3)}
                    className="flex-1"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleBookAppointment}
                    disabled={loading}
                    className="flex-1"
                  >
                    {loading ? 'Booking...' : 'Confirm Appointment'}
                    {!loading && <Check className="ml-2 h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};